import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  materialsService,
  type ConvertMaterialTypeParams,
} from '../services/materials.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Re-file a Brief material between types (view <-> doc) in place.
 *
 * On success the entity moves groups, so we refresh BOTH the project angles
 * (Views) and the brief references (Docs) lists — a conversion to a view
 * returns an angle that must appear immediately, and a conversion to a doc
 * removes the angle and adds a reference. Sketches (controls) are never
 * touched. Errors propagate to the caller, which surfaces `error.detail`
 * verbatim (API-driven message — no client-composed copy).
 */
export function useConvertMaterialType(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['materials', 'convert'],
    meta: {
      lockId: (vars: unknown) =>
        (vars as ConvertMaterialTypeParams).source_id,
    },
    mutationFn: (params: ConvertMaterialTypeParams) =>
      materialsService.convertType(projectId, params),
    onSuccess: () => {
      // Views list (project-scoped angle pool).
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projectAngles.list(projectId),
      });
      // Docs (and every other section) — sweep all reference lists so the
      // moved item shows in the new group and leaves the old one.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
      // A convert can flip the project's `view_promotion` rescue flag (e.g.
      // the only view just became a reference) and the material counters —
      // refresh the project so the Brief banner / Set-up-design hint reconcile.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
    },
  });
}
